import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LoggerService } from '@umanforge/common';
const logger = new LoggerService( 'SelectInput' );
import ValidationIndicator from '@common_components/others/validationIndicator.component';
import InfoTooltip from 'components/infoTooltip.component';
import { CheckIcon, ChevronDownIcon } from '@umanforge/common';

export interface Option {
  id: string;
  label: string;
}

export interface GroupedOption {
  label?: string;
  options: Option[];
}

interface SelectInputProps {
  placeholder?: string;
  isRequired?: boolean;
  emptyGroupsMessage?: string;
  label?: string;
  tooltip?: string;
  labelPosition?: 'top' | 'left';
  className?: string;
  textSize?: string;
  fetchOptions: ( page: number, filter: string ) => Promise<GroupedOption[]>;
  selected?: string;
  onSelect: ( value: string ) => void;
  icon?: React.ReactNode;
  isDisable?: boolean;
  refreshTrigger?: string | number;
  totalNbrOfPages?: number;
}

interface DropdownProps {
  filteredOptions: GroupedOption[];
  selected?: string;
  isTyping: boolean;
  inputValue: string;
  emptyGroupsMessage?: string;
  totalNbrOfPages?: number;
  currentPage: number;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  optionRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  onOptionSelect: ( optionId: string, optionLabel: string ) => void;
  onLoadMore: () => void;
}
const Dropdown: React.FC<DropdownProps> = ( {
  filteredOptions,
  selected,
  isTyping,
  inputValue,
  emptyGroupsMessage,
  totalNbrOfPages,
  currentPage,
  scrollContainerRef,
  optionRefs,
  onOptionSelect,
  onLoadMore
} ) => {
  return (
    <div ref={scrollContainerRef}
      className="mt-2 max-h-80 absolute top-full bg-white min-w-full shadow-md rounded-md w-full z-20 border overflow-y-auto">

      {filteredOptions.length > 0 ? (
        filteredOptions.map( ( group, groupIndex ) => {
          const groupKey = group.label ?? `group-${groupIndex}`;
          return (
            <div
              key={groupKey}
              className="last:mb-0 p-2 gap-2 flex flex-col"
            >
              {group.label && (
                <div className="px-4 py-1 text-sm text-gray-500 font-semibold truncate">
                  {group.label}
                </div>
              )}
              {group.options.map( option => {return (
                <div
                  key={`${groupKey}-${option.id}`}
                  ref={el => {
                    optionRefs.current[ option.id ] = el;
                  }}
                  className={`px-1 whitespace-nowrap flex items-center py-2 cursor-pointer trans-fast rounded-md hover:bg-select-item-bg-hover min-w-0 overflow-hidden ${
                    selected === option.id ? 'bg-select-item-bg text-select-text-color' : ''
                  }`}
                  onClick={e => {
                    e.stopPropagation();
                    onOptionSelect( option.id, option.label );
                  }}
                >
                  <p className="truncate max-w-[40ch]">{option.label}</p>
                  <CheckIcon className={`flex-center w-5 h-5 ${selected === option.id ? 'opacity-100' : 'opacity-0'}`} />
                </div>
              );} )}
            </div>
          );
        } )
      ) : (
        <div className="px-4 py-2 text-sm text-gray-500 font-semibold">
          {isTyping && inputValue.trim() ? 'Aucun résultat trouvé' : emptyGroupsMessage}
        </div>
      )}

      {totalNbrOfPages && currentPage + 1 < totalNbrOfPages && !isTyping && (
        <div
          className="text-center p-2 text-blue-600 cursor-pointer hover:underline"
          onClick={onLoadMore}
        >
          Charger plus...
        </div>
      )}
    </div>
  );
};

const filterOptions = ( groupedOptions: GroupedOption[], searchValue: string ): GroupedOption[] => {
  if ( !searchValue.trim() ) {
    return groupedOptions;
  }

  return groupedOptions.map( group => {
    const filteredGroupOptions = group.options.filter( option => {
      return option.label.toLowerCase().includes( searchValue.toLowerCase() );
    } );

    return {
      ...group,
      options: filteredGroupOptions
    };
  } ).filter( group => {
    return group.options.length > 0;
  } );
};

const getSelectedLabel = ( groupedOptions: GroupedOption[], selected?: string, placeholder?: string ): string => {
  if ( !selected ) {
    return placeholder || '';
  }

  for ( const group of groupedOptions ) {
    const match = group.options.find( option => {return option.id === selected;} );
    if ( match ) {
      return match.label;
    }
  }
  return placeholder || '';
};

const SelectInput: React.FC<SelectInputProps> = ( {
  label,
  tooltip,
  labelPosition = 'left',
  textSize = 'w-3/12 mr-4',
  className = 'w-full max-w-full sm:max-w-[420px]',
  refreshTrigger,
  isRequired = false,
  emptyGroupsMessage,
  placeholder,
  icon,
  fetchOptions,
  totalNbrOfPages,
  selected,
  onSelect,
  isDisable
} ) => {
  const [ isValid, setIsValid ] = useState<boolean>( false );
  const [ isOpen, setIsOpen ] = useState( false );
  const ref = useRef<HTMLDivElement | null>( null );
  const [ isDisabledAuto, setIsDisabledAuto ] = useState( false );
  const [ groupedOptions, setGroupedOptions ] = useState<GroupedOption[]>( [] );
  const [ currentPage, setCurrentPage ] = useState<number>( 0 );
  const [ filteredOptions, setFilteredOptions ] = useState<GroupedOption[]>( [] );
  const [ inputValue, setInputValue ] = useState<string>( '' );
  const [ isTyping, setIsTyping ] = useState<boolean>( false );
  const isComponentDisabled = isDisable || isDisabledAuto;

  const scrollContainerRef = useRef<HTMLDivElement | null>( null );
  const optionRefs = useRef<Record<string, HTMLDivElement | null>>( {} );
  const inputRef = useRef<HTMLInputElement | null>( null );
  const lastSelectedLabelRef = useRef<string>( '' );

  const closeDropdown = useCallback( () => {
    setIsOpen( false );
    setIsTyping( false );

    if ( lastSelectedLabelRef.current ) {
      setInputValue( lastSelectedLabelRef.current );
      return;
    }

    if ( selected ) {
      const selectedLabelFromOptions = getSelectedLabel( groupedOptions, selected, placeholder );
      const selectedLabel = selectedLabelFromOptions || placeholder || '';

      if ( selectedLabel && selectedLabel !== placeholder ) {
        setInputValue( selectedLabel );
        lastSelectedLabelRef.current = selectedLabel;
      }
      return;
    }

    setInputValue( '' );
    lastSelectedLabelRef.current = '';
  }, [ groupedOptions, placeholder, selected ] );

  const handleInputChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    setInputValue( e.target.value );
    setIsTyping( true );
    if ( !isOpen ) {
      setIsOpen( true );
    }
  };
  const handleOptionSelect = ( optionId: string, optionLabel: string ) => {
    onSelect( optionId );
    setIsOpen( false );
    setIsTyping( false );
    setInputValue( optionLabel );
    lastSelectedLabelRef.current = optionLabel;
  };

  useEffect( () => {
    const handleKeyDown = ( event: KeyboardEvent ) => {
      if ( !isOpen || !event.key || event.key.length !== 1 ) {return;}

      const searchLetter = event.key.toLowerCase();
      const flatOptions = filteredOptions.flatMap( group => {return group.options;} );

      const match = flatOptions.find( option =>
      {return option.label.toLowerCase().startsWith( searchLetter );}
      );

      if ( match ) {
        const optionElement = optionRefs.current[ match.id ];
        const container = scrollContainerRef.current;

        if ( optionElement && container ) {
          const optionTop = optionElement.offsetTop;
          container.scrollTo( {
            top: optionTop,
            behavior: 'smooth',
          } );
        }
      }
    };

    document.addEventListener( 'keydown', handleKeyDown );
    return () => {return document.removeEventListener( 'keydown', handleKeyDown );};
  }, [ isOpen, filteredOptions ] );

  useEffect( () => {
    const handleOutsideClick = ( event: MouseEvent | TouchEvent ) => {
      if ( !isOpen || !ref.current ) {
        return;
      }

      const target = event.target as Node | null;
      if ( target && !ref.current.contains( target ) ) {
        closeDropdown();
      }
    };

    // Use capture phase to ensure outside clicks are detected even if a parent stops propagation.
    document.addEventListener( 'mousedown', handleOutsideClick, true );
    document.addEventListener( 'touchstart', handleOutsideClick, true );
    return () => {
      document.removeEventListener( 'mousedown', handleOutsideClick, true );
      document.removeEventListener( 'touchstart', handleOutsideClick, true );
    };
  }, [ closeDropdown, isOpen ] );

  useEffect( () => {
    const filtered = filterOptions( groupedOptions, isTyping ? inputValue : '' );
    setFilteredOptions( filtered );
  }, [ inputValue, groupedOptions, isTyping ] );

  useEffect( () => {
    const loadOptions = async () => {
      const data = await fetchOptions( currentPage, '' );
      setIsDisabledAuto( data.length === 0 );
      if ( currentPage === 0 ) {
        setGroupedOptions( data );
      } else {
        setGroupedOptions( prev => {return [ ...prev, ...data ];} );
      }
    };
    loadOptions();
  }, [ currentPage, refreshTrigger ] );

  useEffect( () => {
    setIsValid( selected != undefined && selected !== '' );
  }, [ selected ] );

  useEffect( () => {
    let cancelled = false;
    const ensureSelectedLabel = async () => {
      if ( !selected ) {
        if ( !isTyping ) { setInputValue( '' ); }
        return;
      }

      // Cherche le label dans les options déjà chargées
      const selectedLabel = getSelectedLabel( groupedOptions, selected, placeholder );
      if ( selectedLabel && selectedLabel !== placeholder ) {
        if ( !isTyping ) { setInputValue( selectedLabel ); }
        // cache for later restore
        lastSelectedLabelRef.current = selectedLabel;
        return;
      }

      // Si non trouvé, tente de récupérer la première page d'options pour résoudre le label
      try {
        const data = await fetchOptions( 0, '' );
        if ( cancelled ) {return;}
        const fetchedLabel = getSelectedLabel( data, selected, placeholder );
        if ( fetchedLabel && fetchedLabel !== placeholder && !isTyping ) {
          setInputValue( fetchedLabel );
        }
      } catch ( e ) {
        logger.warn( 'Error fetching options for selected label', e );
      }
    };

    ensureSelectedLabel();
    return () => { cancelled = true; };
  }, [ selected, groupedOptions, isTyping, placeholder ] );

  const toggleOpen = () => {
    if ( !isComponentDisabled ) {
      if ( !isOpen ) {
        setIsOpen( true );
      } else {
        closeDropdown();
      }
    }
  };

  return (
    <div className={`${labelPosition === 'left' ? 'flex' : ''} ${className}`} ref={ref}>
      {label && (
        <div className={`flex gap-2 items-center ${labelPosition === 'left' ? textSize : 'w-full'} ${labelPosition === 'left' ? '' : 'whitespace-normal'}`}>
          <label className="text-gray-600 truncate">
            {label}
          </label>
          {tooltip && (
            <InfoTooltip label={label} tooltip={tooltip} />
          )}
        </div>
      )}

      <div
        className={`flex w-full py-1 min-h-11 justify-between items-center trans-fast shadow border rounded-md relative
          ${isOpen && 'border-black ring-black ring-1'}
          ${isComponentDisabled ? 'bg-gray-base-veryLight cursor-not-allowed border-gray-base-veryLight' : 'bg-white hover:border-black cursor-pointer'}
        `}
        onClick={toggleOpen}
      >
        <div className='flex w-full h-full items-center'>
          {icon && <div className='text-gray-600 ml-4'>{icon}</div>}
          <ValidationIndicator isRequired={isRequired} isValid={isValid} />
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            placeholder={placeholder}
            className={`truncate w-full ml-4 bg-transparent border-none outline-none ${!inputValue ? 'text-gray-400' : ''} ${isComponentDisabled ? 'cursor-not-allowed' : ''}`}
            onChange={handleInputChange}
            onFocus={() => {
              if ( !isComponentDisabled ) {
                setIsTyping( false );
              }
            }}
            disabled={isComponentDisabled}
            readOnly={false}
          />
          <ChevronDownIcon className={`w-[45px] h-full px-2 rotate-0 trans-fast ${isOpen && 'rotate-180'}`} />
        </div>
        {isOpen && (
          <Dropdown
            filteredOptions={filteredOptions}
            selected={selected}
            isTyping={isTyping}
            inputValue={inputValue}
            emptyGroupsMessage={emptyGroupsMessage}
            totalNbrOfPages={totalNbrOfPages}
            currentPage={currentPage}
            scrollContainerRef={scrollContainerRef}
            optionRefs={optionRefs}
            onOptionSelect={handleOptionSelect}
            onLoadMore={() => {return setCurrentPage( prev => {return prev + 1;} );}}
          />
        )}
      </div>

    </div>
  );
};

export default SelectInput;
